import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { heroImage } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const HeroSection = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <section id="home" className="hero-section relative min-h-screen flex items-center bg-[#1a1c1b] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src={heroImage}
          alt="Creative workspace"
          className="w-full h-full object-cover"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1c1b] via-[#1a1c1b]/90 to-transparent" />
        <div className="absolute inset-0 bg-[#1a1c1b]/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[87.5rem] mx-auto px-10 py-40 w-full">
        <div className="max-w-3xl">
          {/* Label */}
          <div className="mb-6">
            <span className="inline-block bg-[#1e3a5f] text-[#7dd3fc] px-4 py-2 rounded-full text-sm font-semibold uppercase tracking-wider">
              {t.hero.badge}
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="font-black text-[#7dd3fc] uppercase leading-[0.85] mb-8" style={{ fontSize: 'clamp(3.5rem, 5vw + 1rem, 7rem)' }}>
            {t.hero.headline}
          </h1>

          {/* Subheadline */}
          <p className="text-[#7dd3fc]/90 text-xl md:text-2xl font-medium leading-relaxed mb-10 max-w-2xl">
            {t.hero.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4">
            <Button 
              className="bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full px-8 py-6 font-semibold uppercase tracking-wide text-base transition-transform hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2"
              onClick={() => document.getElementById('apps').scrollIntoView({ behavior: 'smooth' })}
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={20} />
            </Button>
            <Button 
              variant="outline"
              className="border-[#7dd3fc] text-[#7dd3fc] hover:bg-[#7dd3fc] hover:text-[#1a1c1b] rounded-full px-8 py-6 font-semibold uppercase tracking-wide text-base transition-all hover:scale-[1.02] active:scale-[0.98]"
              onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
            >
              {t.hero.ctaSecondary}
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <a href="#apps" className="text-[#7dd3fc]/70 hover:text-[#7dd3fc] transition-colors">
          <ChevronDown size={32} />
        </a>
      </div>

      {/* Decorative ribbon */}
      <div className="absolute -right-20 bottom-[20%] w-[600px] h-16 bg-[#7dd3fc] rotate-[-15deg] flex items-center justify-center overflow-hidden">
        <div className="flex gap-8 animate-marquee whitespace-nowrap">
          {[...Array(10)].map((_, i) => (
            <span key={`ribbon-${i}`} className="text-[#1a1c1b] font-bold uppercase text-sm tracking-wider">
              {t.hero.ribbonText}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
